import TextData from 'domain/entity/widgets/TextData';
import GaugeData from 'domain/entity/widgets/GaugeData';
import ChartData, { ChartSerie, ChartSeriesType } from 'domain/entity/widgets/ChartData';
import { WidgetDataTypes } from 'domain/entity/widgetData';
import SingleKPIData from 'domain/entity/widgets/SingleKPIData';
import { WidgetOptionConfigurationField, WidgetType } from 'domain/entity/widget';
import { asWidgetChartLimit, generateChartLimitSeries, JsonChartLimit } from 'adapter/out/client/widgets/data/mappers/common';
import { asWidgetGaugeRanges, JsonGaugeRange } from 'adapter/out/client/widgets/data/mappers/GaugeMapper';

const getRangesOptions = (options: WidgetOptionConfigurationField[], data: GaugeData) =>
    options.filter((option) => option.key === OPTIONS.RANGES).length > 0
        ? asWidgetGaugeRanges(options.filter((option) => option.key === OPTIONS.RANGES)[0].value as JsonGaugeRange[])
        : data.ranges;

const getCheckpointsOptions = (options: WidgetOptionConfigurationField[], data: GaugeData) =>
    options.filter((option) => option.key === OPTIONS.CHECKPOINTS).length > 0
        ? asWidgetChartLimit(options.filter((option) => option.key === OPTIONS.CHECKPOINTS)[0].value as JsonChartLimit[])
        : data.checkpoints;

const getChartLimitOptions = (options: WidgetOptionConfigurationField[], data: ChartData) =>
    options.filter((option) => option.key === OPTIONS.CHECKPOINTS).length > 0
        ? generateChartLimitSeries(
              asWidgetChartLimit(options.filter((option) => option.key === OPTIONS.CHECKPOINTS)[0].value as JsonChartLimit[]),
              data.axesX[0].key,
              data.axesX,
              data.axesY[0].key
          )
        : [];

const getMaxValueOption = (customMax: WidgetOptionConfigurationField, data: GaugeData) => (customMax ? Number(customMax.value) : data.max);
const getMinValueOption = (customMin: WidgetOptionConfigurationField, data: GaugeData) => (customMin ? Number(customMin.value) : data.min);
const getShowAsPercentageValueOption = (
    showAsPercentage: WidgetOptionConfigurationField,
    type: WidgetType,
    data: WidgetDataTypes | Record<string, never>
) => {
    const comparison = type === WidgetType.GAUGE ? (data as GaugeData).comparison : (data as SingleKPIData).comparison;
    return showAsPercentage ? showAsPercentage.value !== ABSOLUTE_PERCENTAGE_VALUES.ABSOLUTE : comparison.showAsPercentage;
};

const getAlignTextOption = (options: WidgetOptionConfigurationField[], name: string) =>
    options.filter((option) => option.key === `${OPTIONS.ALIGN_TEXT}_${name}`).length > 0
        ? (options.filter((option) => option.key === `${OPTIONS.ALIGN_TEXT}_${name}`)[0].value as ALIGN_TEXT)
        : ALIGN_TEXT.START;

export const applyOptions = (data: WidgetDataTypes | Record<string, never>, optionFields: WidgetOptionConfigurationField[]) => {
    const { type } = data;
    if (optionFields.length === 0 || (data && Object.keys(data).length === 0 && data.constructor === Object)) return data;

    switch (type) {
        case WidgetType.TEXT: {
            const textData = data as TextData;

            textData.header.alignText = getAlignTextOption(optionFields, 'header');
            textData.body.alignText = getAlignTextOption(optionFields, 'body');

            return textData;
        }
        case WidgetType.GAUGE: {
            const gaugeData = data as GaugeData;

            gaugeData.comparison.showAsPercentage = getComparisonShowPercentage(optionFields, type, data);
            gaugeData.ranges = getRangesOptions(optionFields, gaugeData);
            gaugeData.checkpoints = getCheckpointsOptions(optionFields, gaugeData);
            gaugeData.min = getMinValueOption(optionFields.filter((option) => option.key === OPTIONS.MIN)[0], gaugeData);
            gaugeData.max = getMaxValueOption(optionFields.filter((option) => option.key === OPTIONS.MAX)[0], gaugeData);

            return mutateDateIfTitleChanges(gaugeData, getTitle(optionFields, data));
        }
        case WidgetType.BAR:
        case WidgetType.LINE:
        case WidgetType.STACKED_BAR: {
            const chartData = data as ChartData;
            const chartLimits =
                type === WidgetType.BAR || type === WidgetType.LINE || type === WidgetType.STACKED_BAR
                    ? getChartLimitOptions(optionFields, data as ChartData)
                    : [];

            chartData.series = [
                ...chartData.series.filter((serie) => serie.type !== ChartSeriesType.CHART_LIMIT),
                ...chartLimits,
            ] as ChartSerie[];

            return mutateDateIfTitleChanges(chartData, getTitle(optionFields, data));
        }
        case WidgetType.SINGLE_KPI: {
            const singleKpiData = data as SingleKPIData;

            const title = getTitle(optionFields, data);
            singleKpiData.text = title;
            singleKpiData.description.title = title; // Does not mutate
            singleKpiData.comparison.showAsPercentage = getComparisonShowPercentage(optionFields, type, data);

            return singleKpiData;
        }
        case WidgetType.PIE:
        case WidgetType.HEATMAP:
        case WidgetType.MULTI_DEVICE_HEATMAP:
        default: {
            const changedData = data as SingleKPIData;

            return mutateDateIfTitleChanges(changedData, getTitle(optionFields, data));
        }
    }
};

const mutateDateIfTitleChanges = (data: WidgetDataTypes | Record<string, never>, title: string) => {
    if (data.description && data.description.title !== title) {
        return { ...data, description: { ...data.description, title } };
    }

    return data;
};

const getTitle = (optionFields: WidgetOptionConfigurationField[], data: WidgetDataTypes | Record<string, never>) => {
    const customTitle = optionFields.filter((option) => option.key === OPTIONS.CUSTOM_TITLE)[0];
    const dataTitle = data instanceof SingleKPIData
        ? data.text
        : (data && data.description && data.description.title) || '';
    return (customTitle ? customTitle.value : dataTitle) as string;
};

const getComparisonShowPercentage = (
    optionFields: WidgetOptionConfigurationField[],
    type: WidgetType,
    data: WidgetDataTypes | Record<string, never>
) => getShowAsPercentageValueOption(optionFields.filter((option) => option.key === OPTIONS.ABSOLUTE_PERCENTAGE)[0], type, data);

export enum ABSOLUTE_PERCENTAGE_VALUES {
    ABSOLUTE = 'absolute',
    PERCENTAGE = 'percentage',
}

export enum OPTIONS {
    CUSTOM_TITLE = 'title',
    RANGES = 'ranges',
    CHECKPOINTS = 'checkpoints',
    MIN = 'min',
    MAX = 'max',
    ALIGN_TEXT = 'align_text',
    // eslint-disable-next-line camelcase
    ALIGN_TEXT_header = 'align_text_header',
    // eslint-disable-next-line camelcase
    ALIGN_TEXT_body = 'align_text_body',
    ABSOLUTE_PERCENTAGE = 'absolute_percentage',
}

export enum ALIGN_TEXT {
    START = 'start',
    CENTER = 'center',
    END = 'end',
    JUSTIFY = 'justify',
}
